import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from '../../utils/i18nStub';
import { CSpinner } from '@coreui/react';

import { fetchEmploymentOverlay, saveEmploymentDraft } from '../../services/employeeEmploymentService';
import { fetchJobs, fetchStaffRelationTypes } from '../../services/employmentLookupsService';

const DraftChip = ({ state }) =>
  (state === 'draft' || state === 'submitted') ? (
    <span className="inline-flex items-center rounded-full bg-sa-100 text-sa-700 text-xs font-semibold px-3 py-1 ms-2">
      مسودة
    </span>
  ) : null;

const EmployeeEmploymentInfo = forwardRef(function EmployeeEmploymentInfo({ active }, ref) {
  const { t, i18n } = useTranslation('employment');
  const preferAr = String(i18n.language).startsWith('ar');

  // ------- caching flags (like other tabs) -------
  const overlayLoadedRef = useRef(false);
  const lookupsLoadedRef = useRef(false);

  // ------- state -------
  const [overlay, setOverlay] = useState({ data: {}, flags: {}, pendingCountTab: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
    contractual_relationship_code: '',
    actual_job_id: '',
    position_according_to_contract_job_id: '',
    hiring_date: '',
    has_transfer_letter: false,
  });

  const [jobs, setJobs] = useState([]);         // [{ id, name }]
  const [relations, setRelations] = useState([]); // [{ code, name_ar, name_en }]

  // ------- helpers -------
  const resetFormFrom = (d) => {
    setForm({
      contractual_relationship_code: d.contractual_relationship_code ?? '',
      actual_job_id: d.actual_job_id ?? '',
      position_according_to_contract_job_id: d.position_according_to_contract_job_id ?? '',
      hiring_date: d.hiring_date ?? '',
      has_transfer_letter: d.has_transfer_letter === 'yes',
    });
  };

  const ensureLookups = async () => {
    if (lookupsLoadedRef.current && jobs.length && relations.length) return;
    const jobsPromise = jobs.length ? Promise.resolve(jobs) : fetchJobs();
    const relsPromise = relations.length ? Promise.resolve(relations) : fetchStaffRelationTypes();
    const [j, r] = await Promise.all([jobsPromise, relsPromise]);
    if (!jobs.length) setJobs(j || []);
    if (!relations.length) setRelations(r || []);
    lookupsLoadedRef.current = true;
  };

  const loadOverlay = async () => {
    setLoading(true);
    try {
      const res = await fetchEmploymentOverlay();
      setOverlay(res || { data: {}, flags: {}, pendingCountTab: 0 });
      resetFormFrom((res && res.data) || {});
    } finally {
      setLoading(false);
    }
  };

  // ------- initial load per activation (cached) -------
  useEffect(() => {
    if (!active) return;
    (async () => {
      await ensureLookups();
      if (!overlayLoadedRef.current) {
        await loadOverlay();
        overlayLoadedRef.current = true; // cache: don’t refetch on revisit
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ------- expose to parent (same as other tabs) -------
  useImperativeHandle(ref, () => ({
    async refreshOverlay() {
      await loadOverlay();             // explicit refresh hits server
      overlayLoadedRef.current = true; // keep cached after refresh
    },
    getOverlaySnapshot() {
      return { data: overlay?.data || {}, flags: overlay?.flags || {} };
    },
  }));

  // ------- save draft -------
  const onSave = async () => {
    setLoading(true);
    try {
      const payload = {
        contractual_relationship_code: form.contractual_relationship_code || undefined,
        actual_job_id: form.actual_job_id || undefined,
        position_according_to_contract_job_id: form.position_according_to_contract_job_id || undefined,
        hiring_date: form.hiring_date || undefined,  // yyyy-MM-dd
        has_transfer_letter: form.has_transfer_letter, // service converts to "yes"/"no"
      };
      const updated = await saveEmploymentDraft(payload);
      setOverlay(updated || { data: {}, flags: {} });
      resetFormFrom((updated && updated.data) || {});
      setEditing(false);
      overlayLoadedRef.current = true;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ------- display helpers -------
  const jobName = (id) => {
    if (id === null || id === undefined || id === '') return null;
    const hit = jobs.find(x => String(x.id) === String(id));
    return hit?.name ?? id;
  };

  const relationName = (code) => {
    if (!code) return null;
    const hit = relations.find(x => String(x.code) === String(code));
    if (!hit) return code;
    return preferAr ? (hit.name_ar || hit.name_en || code)
                    : (hit.name_en || hit.name_ar || code);
  };

  const d = overlay?.data || {};
  const f = overlay?.flags || {};
  const tabPending = (overlay?.pendingCountTab ?? 0) || (overlay?.pendingCountEmployment ?? 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-3">
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {t('edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
            >
              {loading ? <CSpinner size="sm" /> : t('save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); resetFormFrom(d); }}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      {tabPending > 0 && (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3">
          {`${tabPending} ${t('unsent_changes', 'تغييرات غير مُرسلة')}`}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-6">
          <CSpinner />
        </div>
      )}

      {!loading && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" dir="rtl">
            <div className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2 justify-end">
                {t('contractual_relationship', 'العلاقة التعاقدية')}
                <DraftChip state={f.contractual_relationship_code} />
              </span>
              <select
                value={form.contractual_relationship_code || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, contractual_relationship_code: event.target.value }))}
                disabled={!editing}
                className={`rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!editing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              >
                <option value="">{t('select_relationship', 'اختر العلاقة')}</option>
                {relations.map((rel) => (
                  <option key={rel.code} value={rel.code}>
                    {preferAr ? rel.name_ar : rel.name_en}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                {t('actual_job', 'الوظيفة الحالية')}
                <DraftChip state={f.actual_job_id} />
              </span>
              <select
                value={form.actual_job_id || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, actual_job_id: event.target.value }))}
                disabled={!editing}
                className={`rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!editing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              >
                <option value="">{t('select_job', 'اختر الوظيفة')}</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.name}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                {t('contract_job', 'الوظيفة حسب العقد')}
                <DraftChip state={f.position_according_to_contract_job_id} />
              </span>
              <select
                value={form.position_according_to_contract_job_id || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, position_according_to_contract_job_id: event.target.value }))}
                disabled={!editing}
                className={`rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!editing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              >
                <option value="">{t('select_job', 'اختر الوظيفة')}</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.name}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                {t('hiring_date', 'تاريخ التعيين')}
                <DraftChip state={f.hiring_date} />
              </span>
              <input
                type="date"
                value={form.hiring_date || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, hiring_date: event.target.value }))}
                disabled={!editing}
                className={`rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!editing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              />
            </div>

            <div className="md:col-span-2 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3">
              <div className="flex flex-col text-right">
                <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                  {t('transfer_letter', 'خطاب نقل')}
                  <DraftChip state={f.has_transfer_letter} />
                </span>
                <span className="text-gray-600 text-sm">
                  {t('transfer_letter_hint', 'حدد الخيار في حالة وجود خطاب نقل صادر من الجهة.')}
                </span>
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  checked={!!form.has_transfer_letter}
                  onChange={(event) => setForm((prev) => ({ ...prev, has_transfer_letter: event.target.checked }))}
                  disabled={!editing}
                  className="h-5 w-5 rounded border-gray-300 text-sa-600 focus:ring-sa-500"
                />
                {form.has_transfer_letter ? t('yes', 'نعم') : t('no', 'لا')}
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default EmployeeEmploymentInfo;
